import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
} from '@ea-lab/reactive-json';
import parse, { domToReact } from 'html-react-parser';
import { Pencil, Trash2, X, Check, Loader2 } from 'lucide-react';
import { saveDocComment, deleteDocComment, fetchCollection } from '../../../services/api';

const SAFE_TAGS = new Set([
  'p', 'strong', 'em', 'b', 'i', 'u', 'a', 'br', 'ul', 'ol', 'li',
  'span', 'sub', 'sup', 's', 'mark', 'code',
]);

function isSafeUrl(url) {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch { return false; }
}

function buildReplaceOptions() {
  return {
    replace(domNode) {
      if (domNode.type !== 'tag') return;
      if (!SAFE_TAGS.has(domNode.name)) {
        return <>{domToReact(domNode.children, buildReplaceOptions())}</>;
      }
      if (domNode.name === 'a') {
        const href = domNode.attribs?.href || '';
        if (!isSafeUrl(href)) {
          return <span>{domToReact(domNode.children, buildReplaceOptions())}</span>;
        }
      }
    },
  };
}

function sanitizeHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    if (!SAFE_TAGS.has(node.tagName.toLowerCase())) {
      return Array.from(node.childNodes).map(walk).join('');
    }
    const el = document.createElement(node.tagName.toLowerCase());
    if (el.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (isSafeUrl(href)) el.setAttribute('href', href);
    }
    el.innerHTML = Array.from(node.childNodes).map(walk).join('');
    return el.outerHTML;
  };
  return Array.from(doc.body.childNodes).map(walk).join('');
}

function processRawComments(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item?.data)
    .map((item) => {
      const d = item.data;
      return {
        id: d.id,
        body: d.body || '',
        author: d.author || '',
        pageId: d.pageId ? parseInt(d.pageId, 10) : null,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      };
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(dateStr));
  } catch { return dateStr; }
}

/**
 * Renders a single comment with body display, edit (contentEditable),
 * and delete (with confirmation). Edit/delete visible only for the author.
 *
 * YAML usage (inside Switch singleOption):
 *   type: DocCommentItem
 *   currentUser: ~~.userInfo.name
 *   pageId: ~~.page.data.id
 *   commentsPath: "comments"
 */
export const DocCommentItem = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const ev = (val) => evaluateTemplateValue({ valueToEvaluate: val, globalDataContext, templateContext });

  const id = ev('~.id');
  const body = ev('~.body') || '';
  const author = ev('~.author') || '';
  const createdAt = ev('~.createdAt') || '';
  const currentUser = ev(props.currentUser) || '';
  const pageId = ev(props.pageId);
  const commentsPath = props.commentsPath || 'comments';

  const isOwner = Boolean(currentUser && author === currentUser);

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const editRef = useRef(null);

  const refreshComments = useCallback(async () => {
    const params = new URLSearchParams({ display: 'decoupled', 'filter[0][f]': 'entity_id', 'filter[0][v]': String(pageId) });
    const url = `/decoupled-api/comment/doc_comment/collection?${params}`;
    const raw = await fetchCollection(url, true);
    globalDataContext.updateData(processRawComments(raw), commentsPath);
    window.dispatchEvent(new CustomEvent('doc:comments-updated'));
  }, [pageId, commentsPath, globalDataContext]);

  const handleSave = useCallback(async () => {
    if (!editRef.current) return;
    const newBody = editRef.current.innerHTML;
    if (!newBody.trim() || newBody === '<br>') return;

    setStatus('saving');
    setError('');
    try {
      await saveDocComment({ id, body: newBody, pageId: String(pageId) });
      await refreshComments();
      setEditing(false);
    } catch (e) {
      setError(e.message || 'Erreur lors de la modification.');
    } finally {
      setStatus(null);
    }
  }, [id, pageId, refreshComments]);

  const handleDelete = useCallback(async () => {
    setStatus('deleting');
    setError('');
    try {
      await deleteDocComment(id);
      await refreshComments();
    } catch (e) {
      setError(e.message || 'Erreur lors de la suppression.');
    } finally {
      setStatus(null);
      setConfirmDelete(false);
    }
  }, [id, refreshComments]);

  const initial = (author || 'U').charAt(0).toUpperCase();

  const parsedBody = useMemo(
    () => body ? parse(body, buildReplaceOptions()) : null,
    [body],
  );

  return (
    <ActionDependant {...props}>
      <div className="pb-5 border-b border-stone-100 last:border-0 last:pb-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-xs font-semibold text-white shadow-sm">
              {initial}
            </div>
            <div>
              <span className="font-medium text-stone-800 text-sm block">{author}</span>
              <span className="text-2xs text-stone-400">{formatDate(createdAt)}</span>
            </div>
          </div>

          {isOwner && !editing && !confirmDelete && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => { setEditing(true); setConfirmDelete(false); setError(''); }}
                className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                aria-label="Modifier"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="p-1.5 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                aria-label="Supprimer"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>

        {confirmDelete && (
          <div className="alert alert-danger items-center mb-3">
            <span className="text-sm text-red-700 flex-1">Supprimer ce commentaire ?</span>
            <button
              type="button"
              onClick={handleDelete}
              disabled={status === 'deleting'}
              className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1"
            >
              {status === 'deleting' ? <Loader2 size={12} className="animate-spin" /> : null}
              Confirmer
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="px-3 py-1.5 text-xs font-medium text-stone-600 bg-white border border-stone-200 rounded-lg hover:bg-stone-50"
            >
              Annuler
            </button>
          </div>
        )}

        {editing ? (
          <div>
            <div
              ref={editRef}
              contentEditable
              suppressContentEditableWarning
              className="min-h-20 p-3 border border-stone-200 rounded-xl text-sm text-stone-700 prose prose-sm prose-stone max-w-none focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData.getData('text/plain');
                document.execCommand('insertText', false, text);
              }}
            />
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={status === 'saving'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-stone-900 rounded-lg hover:bg-stone-800 disabled:opacity-50"
              >
                {status === 'saving' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Enregistrer
              </button>
              <button
                type="button"
                onClick={() => { setEditing(false); setError(''); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-600 bg-white border border-stone-200 rounded-lg hover:bg-stone-50"
              >
                <X size={12} /> Annuler
              </button>
            </div>
          </div>
        ) : (
          <div className="text-stone-700 text-sm prose prose-sm prose-stone max-w-none">
            {parsedBody || (
              <span className="text-stone-400 italic">Aucun contenu</span>
            )}
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 mt-2">{error}</p>
        )}
      </div>
    </ActionDependant>
  );
};
